import {Box, ListItemIcon, ListItemText} from "@mui/material";
import {styled} from "@mui/material/styles";
import {gql, useMutation, useSubscription} from "@apollo/client";
import {useRouter} from "next/router";
import {useKeycloak} from "../../src/useKeycloak";
import {generateStaticColorFromUserId, generateStaticUserStringFromUser, isNotNullOrEmpty} from "../../utils";
import StaticAvatar from "../StaticAvatar";

import {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUsers} from "@fortawesome/pro-solid-svg-icons";
import {faHeartCircle} from "@fortawesome/pro-duotone-svg-icons";
import {useTranslation} from "react-i18next";
import ListItemSecondaryActionTime from "../ListItems/ListItemSecondaryActionTime";
import {ListItemButtonWrapper, ListItemWrapper, ListWrapper} from "../../styles/ListComponents";

export const SUB_MESSAGES = gql`
subscription MessagesSubscription($userId: uuid = "") {
  messagingActivities(orderBy: {title: ASC}) {
    id
    updatedAt
    title
    activityType
    messages(limit: 1, orderBy: {createdAt: DESC}) {
      id
      updatedAt
      content
      userId
      userTable {
        id
        lastSeen
        firstName
        lastName
        emailAddress
        image
      }
      message_reads(where: {userId: {_eq: $userId}}) {
        read
        userId
        id
      }
    }
    activityUsers {
      user {
        id
        firstName
        lastName
        emailAddress
        username
      }
    }
  }
}
`;

const MARK_AS_READ = gql`
mutation MyMutation($messageId: uuid = "") {
	createMessagingMessageRead(
		object: {messageId: $messageId, read: true}, 
		onConflict: {
			constraint: message_read_message_id_user_id_key, 
			updateColumns: read
		}
	) 
	{
		id
	}
}
`;

/*
const SUB_UNREAD_MESSAGES = gql`
subscription unreadMessagesSubscription($roomId: String = "", $userId: uuid = "") {
	notificationNotificationSends(where: {doNavigateParams: {_eq: $roomId}, _and: {userIdTo: {_eq: $userId}}}, orderBy: {updatedAt: DESC}, limit: 1) {
		seen
	}
}
`;
 */

const ListItemIconWrapper = styled(ListItemIcon)({
	minWidth: 38
});

const ListItemMessageActivity = ({messageActivity}) =>	{
	const router = useRouter();
	const {tokenParsed} = useKeycloak();
	const { t } = useTranslation();
	const [markAsRead] = useMutation(MARK_AS_READ);
	const [active, setActive] = useState(false);

	useEffect(() => {
		router.events.on("routeChangeComplete", (url) => {
			const id = url?.split("/").pop();
			setActive(id === messageActivity?.id);
		});
	}, []);

	useEffect(() => {
	}, [messageActivity]);
	let title, icon = "";

	if (messageActivity?.activityType === "INSTANT") {
		// Instant message

		const chatPartner = messageActivity.activityUsers.find((activityUser) => activityUser.user?.id !== tokenParsed.sub)?.user;
		title = generateStaticUserStringFromUser(chatPartner);
		icon = <StaticAvatar
			user={chatPartner}
		/>;
	}

	else {
		// Group message
		const staticBackgroundColor = generateStaticColorFromUserId(messageActivity?.id);
		title = messageActivity?.title;
		icon = <Box sx={{background: staticBackgroundColor, borderRadius: "50%", width: "36px", height: "36px", alignItems: "center", display: "flex", justifyContent: "center"}}>
			<FontAwesomeIcon icon={faUsers} size={"lg"} color={"white"}/>
		</Box>;
	}

	const onListItemClick = () => {
		if (messageActivity?.messages[0]?.id) {
			markAsRead({
				variables: {
					messageId: messageActivity?.messages[0]?.id

				}
			});
		}
		router.push("/message/" + messageActivity?.id);
	};

	let hasReadMessage = false;

	if (messageActivity?.messages?.length > 0) {
		const isMyMessage = messageActivity?.messages[0]?.userId === tokenParsed.sub;
		if (isMyMessage) {
			hasReadMessage = true;
		}
		else if ( messageActivity?.messages[0]?.message_reads.length > 0 ) {
			const iHaveReadThis = messageActivity?.messages[0]?.message_reads.find((messageRead) => messageRead.userId === tokenParsed.sub);
			if (iHaveReadThis?.read) {
				hasReadMessage = true;
			}
		}
	}
	else {
		// det er ingen meldinger i denne samtalen
		hasReadMessage = true;
	}

	// console.log("Token: " + tokenParsed.sub);

	let secondaryText = "";
	if (messageActivity && messageActivity.messages && messageActivity.messages.length > 0) {
		if (messageActivity?.messages[0]?.content) {
			// console.log("MessageActivity ", messageActivity?.messages[0]?.content);
			if (messageActivity.messages[0].userTable && messageActivity.messages[0].userTable.id === tokenParsed.sub) {
				secondaryText = t("You") + ": " + messageActivity.messages[0].content;
			}
			else {
				secondaryText = messageActivity.messages[0].userTable.firstName + ": " + messageActivity.messages[0].content;
			}
		}
		secondaryText = secondaryText.length > 26 ? secondaryText.substring(0, 26) + "..." : secondaryText;
	}

	const titleText = () => {
		if (messageActivity?.activityType === "ORGANIZATIONROOM") {
			const heartIcon = <FontAwesomeIcon icon={faHeartCircle} color={"rgb(10, 132, 255)"} style={{paddingLeft: "8px"}} swapOpacity/>;
			return (
				<>
					{title}
					{heartIcon}
				</>
			);
		}
		else {
			return (
				title
			);
		}
	};

	return (
		<ListItemWrapper key={messageActivity?.id || crypto.randomUUID()}
						 onClick={() => onListItemClick()}
						 secondaryAction={
								 <ListItemSecondaryActionTime time={messageActivity?.messages[0]?.updatedAt}/>
							 						 }
		>
			<ListItemButtonWrapper
				height={50}
				active={active.toString()}
			>
				<ListItemIconWrapper>{icon}</ListItemIconWrapper>
				<ListItemText
					sx={{ml: 2, mr: 1}}
					primary={titleText()}
					secondary={secondaryText}
					slotProps={{
						primary: {
							fontWeight: "bold",
							color: "textPrimary",
							variant: "h5",
							noWrap: true
						},
						secondary: {
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap"
						}
					}}
				/>
			</ListItemButtonWrapper>
		</ListItemWrapper>
	);
};

function Messages({activeTab, searchString}) {
	const {tokenParsed} = useKeycloak();
	const [messageList, setMessageList] = useState([]);
	const {data, loading, error} = useSubscription(SUB_MESSAGES, { variables: { userId: tokenParsed.sub }});
	useEffect(() => {
		if (data) {
			filterMessages();
		}
	}, [data]);

	useEffect(() => {
		filterMessages();
	}, [searchString]);

	const filterMessages = () => {
		let fmsl;

		if (isNotNullOrEmpty(searchString)) {
			fmsl = data?.messagingActivities.filter((msg) => {
				if (msg.activityType === "INSTANT") {
					const chatPartner = msg.activityUsers.find((activityUser) => activityUser.user?.id !== tokenParsed.sub)?.user;
					const chatPartnerName = generateStaticUserStringFromUser(chatPartner);
					return chatPartnerName.toLowerCase().includes(searchString.toLowerCase());
				}
				else {
					return msg.title.toLowerCase().includes(searchString.toLowerCase());
				}
			});
		}
		else {
			// henter ut hovedgruppen
			const orgRoom = data?.messagingActivities.find((msg) => msg.activityType === "ORGANIZATIONROOM");

			if (activeTab === 0) {
				fmsl = data?.messagingActivities;
			}
			else if (activeTab === 1) {
				fmsl = data?.messagingActivities.filter((msg) => msg.activityType === "INSTANT");
			}
			else if (activeTab === 2) {
				fmsl = data?.messagingActivities.filter((msg) => msg.activityType === "ORGANIZATIONROOM" || msg.activityType === "GROUP");
			}

			if (fmsl !== undefined && fmsl[0]?.activityType !== "ORGANIZATIONROOM") {
				// hvis ikke hovedgruppen er første element så sletter vi den i fra listen og legger den til først,
				// etter at resten av elementene er sortert basert på når de sist ble oppdatert med melding

				fmsl = fmsl.filter((msg) => msg?.id !== orgRoom?.id);
			}

			const sortedfmsl = fmsl?.sort((a, b) => {
				const aTime = a.messages.length > 0 ? new Date(a.messages[0].updatedAt) : new Date(a.updatedAt);
				const bTime = b.messages.length > 0 ? new Date(b.messages[0].updatedAt) : new Date(b.updatedAt);
				return bTime - aTime;
			});
			fmsl = sortedfmsl;
			// legger til hovedgruppen først i listen
			if (activeTab !== 1) {
				fmsl = fmsl?.filter((msg) => msg?.id !== orgRoom?.id);
				fmsl?.unshift(orgRoom);
			}
		}

		setMessageList(fmsl);
	};

	useEffect(() => {
		filterMessages();
	}, [activeTab]);

	if (loading) return (<div />);

	return (
		messageList && messageList?.length > 0 ?
			<ListWrapper disablePadding component="nav">
				{messageList?.map((messageActivity) => (
					<ListItemMessageActivity key={messageActivity?.id || crypto.randomUUID()} messageActivity={messageActivity}/>
				))}
			</ListWrapper>
			:
			null
	);
}

export default Messages;
